// src/pages/Dashboard.js (with API integration)
import React, { useState, useEffect } from 'react';
import { 
  FaCogs, FaBox, FaTags, FaBoxOpen, FaMoneyBillWave,
  FaCheckCircle, FaTimesCircle, FaCalendarDay, FaShoppingCart,
  FaUsers, FaChartLine, FaMotorcycle, FaSpinner, FaExclamationTriangle
} from 'react-icons/fa';
import SummaryApi from '../common';
import displayINRCurrency from '../helpers/displayCurrency';

const Dashboard = () => {
  // State for dashboard data
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    overallCounts: {
      categories: 0,
      brands: 0,
      deliveryPartners: 0,
      products: 0,
      customers: 0,
      orders: 0,
    },
    totalRevenue: 0,
    todayRevenue: 0,
    todayOrdersCount: 0,
    TotalDeliveredOrdersCount: 0,
    TotalCancelledOrdersCount: 0,
    todayOrders: [],
    recentOrders: []
  });

  const [todayOrdersLoading, setTodayOrdersLoading] = useState(false);
  const [todayOrdersError, setTodayOrdersError] = useState(null);

  // Helper function to get today's date range
  const getTodayDateRange = () => {
    const today = new Date();
    const startOfDay = new Date(today);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    return { start: startOfDay, end: endOfDay };
  };

  // Get order date from order object
  const getOrderDate = (order) => {
    if (order.order_date && order.order_date !== '0000-00-00' && order.order_date !== 'NULL') {
      const date = new Date(order.order_date);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    if (order.order_date_time && order.order_date_time !== '0000-00-00 00:00:00' && order.order_date_time !== 'NULL') {
      const date = new Date(order.order_date_time);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    if (order.created_date && order.created_date !== '0000-00-00' && order.created_date !== 'NULL') {
      const date = new Date(order.created_date);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    return null;
  };

  // Fetch today's orders
  const fetchTodayOrders = async () => {
    setTodayOrdersLoading(true);
    setTodayOrdersError(null);
    
    try {
      // First, get all orders
      const response = await fetch(SummaryApi.allOrder.url, {
        method: SummaryApi.allOrder.method,
        headers: {
          'Authorization': SummaryApi.bearerToken,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch orders: ${response.status}`);
      }

      const result = await response.json();
      console.log('All orders response for dashboard:', result);

      let allOrders = [];
      if (result.status && result.data && Array.isArray(result.data)) {
        allOrders = result.data;
      } else if (Array.isArray(result)) {
        allOrders = result;
      } else {
        throw new Error('Unexpected data format from server');
      }

      // Filter today's orders
      const todayRange = getTodayDateRange();
      const todayOrdersList = allOrders.filter(order => {
        const orderDate = getOrderDate(order);
        if (!orderDate || isNaN(orderDate.getTime())) {
          return false;
        }
        return orderDate >= todayRange.start && orderDate <= todayRange.end;
      });

      // Calculate today's revenue
      const todayRevenue = todayOrdersList.reduce((sum, order) => {
        return sum + parseFloat(order.total_amount || 0);
      }, 0);

      // Calculate overall statistics
      const totalRevenue = allOrders.reduce((sum, order) => {
        return sum + parseFloat(order.total_amount || 0);
      }, 0);

      const deliveredOrders = allOrders.filter(order => order.order_status === 'Delivered');
      const cancelledOrders = allOrders.filter(order => order.order_status === 'Cancelled');

      // Get unique customers (assuming customer_id is unique)
      const uniqueCustomerIds = [...new Set(allOrders.map(order => order.customer_id).filter(id => id))];
      
      // Get recent orders (last 10 orders)
      const recentOrders = [...allOrders]
        .sort((a, b) => {
          const dateA = getOrderDate(a);
          const dateB = getOrderDate(b);
          return dateB - dateA;
        })
        .slice(0, 10);

      // Update dashboard data
      setDashboardData({
        overallCounts: {
          categories: 8, // Static for now
          brands: 12, // Static for now
          deliveryPartners: 6, // Static for now
          products: 42, // Static for now
          customers: uniqueCustomerIds.length,
          orders: allOrders.length,
        },
        totalRevenue,
        todayRevenue,
        todayOrdersCount: todayOrdersList.length,
        TotalDeliveredOrdersCount: deliveredOrders.length,
        TotalCancelledOrdersCount: cancelledOrders.length,
        todayOrders: todayOrdersList.slice(0, 10), // Show only first 10 today's orders
        recentOrders
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setTodayOrdersError(error.message);
    } finally {
      setTodayOrdersLoading(false);
      setLoading(false);
    }
  };

  // Fetch initial data
  useEffect(() => {
    fetchTodayOrders();
  }, []);

  // Refresh today's orders
  const handleRefreshTodayOrders = () => {
    fetchTodayOrders();
  };

  // Beautiful Gradient Stat Cards
  const statCards = [
    {
      title: 'Total Revenue',
      value: dashboardData.totalRevenue,
      icon: FaMoneyBillWave,
      gradient: 'from-purple-500 via-purple-600 to-purple-700',
      iconColor: 'text-purple-100',
      description: 'Total revenue generated'
    },
    {
      title: 'Total Orders',
      value: dashboardData.overallCounts.orders,
      icon: FaShoppingCart,
      gradient: 'from-blue-500 via-blue-600 to-blue-700',
      iconColor: 'text-blue-100',
      description: 'All-time orders'
    },
    {
      title: 'Total Customers',
      value: dashboardData.overallCounts.customers,
      icon: FaUsers,
      gradient: 'from-green-500 via-green-600 to-green-700',
      iconColor: 'text-green-100',
      description: 'Registered customers'
    },
    {
      title: "Today's Revenue",
      value: dashboardData.todayRevenue,
      icon: FaChartLine,
      gradient: 'from-orange-500 via-orange-600 to-red-600',
      iconColor: 'text-orange-100',
      description: 'Revenue today'
    }
  ];

  // Gradient Quick Stats Cards
  const quickStats = [
    { 
      label: 'Products', 
      value: dashboardData.overallCounts.products, 
      icon: FaBoxOpen, 
      gradient: 'from-amber-500 to-orange-600',
      iconBg: 'bg-amber-100'
    },
    { 
      label: 'Delivery Partners',
      value: dashboardData.overallCounts.deliveryPartners, 
      icon: FaMotorcycle,
      gradient: 'from-rose-500 to-pink-600',
      iconBg: 'bg-rose-100'
    },
    // { 
    //   label: 'Delivered Orders',
    //   value: dashboardData.TotalDeliveredOrdersCount, 
    //   icon: FaCheckCircle,
    //   gradient: 'from-emerald-500 to-teal-600',
    //   iconBg: 'bg-emerald-100'
    // },
    // { 
    //   label: 'Cancelled Orders',
    //   value: dashboardData.TotalCancelledOrdersCount, 
    //   icon: FaTimesCircle,
    //   gradient: 'from-rose-500 to-red-600',
    //   iconBg: 'bg-rose-100'
    // },
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
      'Order placed': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
      'Processing': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
      'Order confirmed': { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200' },
      'Order in transit': { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200' },
      'Delivered': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      'Cancelled': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
    };
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
        {status}
      </span>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Sridanth Dashboard
            </h1>
            <p className="text-gray-600 mt-2">Manufacturing & Sales Overview</p>
          </div>
          <div className="mt-4 md:mt-0">
            <button
              onClick={handleRefreshTodayOrders}
              disabled={todayOrdersLoading}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow hover:shadow-lg transition-all duration-200 flex items-center"
            >
              {todayOrdersLoading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <FaChartLine className="mr-2" />
                  Refresh Data
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {todayOrdersError && (
        <div className="mb-6 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4 shadow-lg">
          <div className="flex items-center">
            <FaExclamationTriangle className="text-red-500 mr-3" />
            <div>
              <h3 className="font-semibold text-red-800">Error Loading Data</h3>
              <p className="text-red-600 text-sm mt-1">{todayOrdersError}</p>
              <button
                onClick={handleRefreshTodayOrders}
                className="mt-2 bg-red-100 hover:bg-red-200 text-red-800 font-medium py-1 px-3 rounded text-sm transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Stats Grid with Beautiful Gradients */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div 
            key={index} 
            className={`bg-gradient-to-br ${stat.gradient} rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-white/80 text-sm font-medium mb-1">{stat.title}</p>
                <p className="text-2xl font-bold mb-2">
                  {stat.title.includes('Revenue') ? displayINRCurrency(stat.value) : stat.value.toLocaleString()}
                </p>
                <p className="text-white/70 text-xs mb-2">{stat.description}</p>
              </div>
              <div className={`p-4 rounded-xl bg-white/20 backdrop-blur-sm`}>
                <stat.icon className={`text-2xl ${stat.iconColor}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Quick Stats with Gradient Cards */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>
            <div className="space-y-4">
              {quickStats.map((stat, index) => (
                <div 
                  key={index}
                  className={`bg-gradient-to-r ${stat.gradient} rounded-xl p-4 text-white transform hover:scale-102 transition-all duration-200 shadow-lg`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-lg ${stat.iconBg} mr-3`}>
                        <stat.icon className="text-lg text-gray-700" />
                      </div>
                      <span className="font-medium">{stat.label}</span>
                    </div>
                    <span className="text-xl font-bold">{stat.value.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Today's Summary Cards */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Today's Overview</h2>
              <div className="flex items-center text-green-600">
                <FaCalendarDay className="mr-2" />
                <span className="text-sm font-medium">{new Date().toLocaleDateString('en-IN')}</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-gray-700">Today's Orders</span>
                <span className="font-bold text-blue-600">{dashboardData.todayOrdersCount}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-gray-700">Today's Revenue</span>
                <span className="font-bold text-green-600">{displayINRCurrency(dashboardData.todayRevenue)}</span>
              </div>
              {/* <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                <span className="text-gray-700">Avg. Order Value</span>
                <span className="font-bold text-purple-600">
                  {dashboardData.todayOrdersCount > 0 
                    ? displayINRCurrency(dashboardData.todayRevenue / dashboardData.todayOrdersCount)
                    : '₹ 0'}
                </span>
              </div> */}
            </div>
          </div>
          {/* <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg">
                <span className="text-gray-700">Delivery Rate</span>
                <span className="font-bold text-indigo-600">
                  {dashboardData.overallCounts.orders > 0 
                    ? `${((dashboardData.TotalDeliveredOrdersCount / dashboardData.overallCounts.orders) * 100).toFixed(1)}%`
                    : '0%'}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-teal-50 rounded-lg">
                <span className="text-gray-700">Cancellation Rate</span>
                <span className="font-bold text-teal-600">
                  {dashboardData.overallCounts.orders > 0 
                    ? `${((dashboardData.TotalCancelledOrdersCount / dashboardData.overallCounts.orders) * 100).toFixed(1)}%`
                    : '0%'}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                <span className="text-gray-700">Customer Growth</span>
                <span className="font-bold text-amber-600">
                  +12.5%
                </span>
              </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* Today's Orders Section */}
      <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-xl border border-blue-100 mb-8">
        <div className="p-6 border-b border-blue-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <div className="flex items-center">
                <h2 className="text-xl font-semibold text-gray-900">Today's Orders</h2>
                <span className="ml-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {dashboardData.todayOrdersCount} Orders
                </span>
              </div>
              <p className="text-gray-600 mt-1">Orders placed today ({new Date().toLocaleDateString('en-IN')})</p>
            </div>
            <div className="bg-gradient-to-r from-yellow-400 to-amber-500 rounded-xl px-4 py-3 mt-4 md:mt-0 shadow-lg">
              <div className="flex items-center">
                <span className="text-white font-medium mr-2">Today's Revenue:</span>
                <span className="text-lg font-bold text-white">
                  {displayINRCurrency(dashboardData.todayRevenue)}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {todayOrdersLoading ? (
            <div className="text-center py-12">
              <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading today's orders...</p>
            </div>
          ) : dashboardData.todayOrders.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FaShoppingCart className="text-gray-400 text-2xl" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No orders today</h3>
              <p className="text-gray-600">No orders have been placed yet today.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Order Code</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Customer Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Mobile</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Order Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {dashboardData.todayOrders.map((order, index) => {
                    const orderDate = getOrderDate(order);
                    return (
                      <tr key={index} className="hover:bg-blue-50 transition-colors duration-150">
                        <td className="px-4 py-4 text-sm font-medium text-blue-600">{order.order_code || 'N/A'}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{order.customer_name || 'N/A'}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{order.mobile_number || 'N/A'}</td>
                        <td className="px-4 py-4 text-sm font-semibold text-gray-900">
                          {displayINRCurrency(parseFloat(order.total_amount || 0))}
                        </td>
                        <td className="px-4 py-4">
                          {getStatusBadge(order.order_status || 'Pending')}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {orderDate ? orderDate.toLocaleTimeString('en-IN', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true 
                          }) : 'N/A'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;